import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import {
  getToolboxToken, getNcToken,
  type NowCertsClient, type PremFiInsuredData,
} from './premiumFinanceScanShared';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE = 10;

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed:  { label: 'Failed',  color: '#f87171' },
};

const formatDate = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

const showCompanyName = (name: string) => name === 'CapitalPremiumFinancing' ? 'Capital Premium Financing' : name;

/* Reads a value trying multiple casings/shapes — the legacy `responseJson`
 * blob has shipped in flat-camelCase, flat-PascalCase, and nested
 * grossPremium[]-PascalCase variants at different times. */
function pick(data: any, ...paths: string[]): any {
  for (const p of paths) {
    let cur = data;
    for (const part of p.split('.')) {
      if (cur == null) { cur = undefined; break; }
      const m = part.match(/^(\w+)\[(\d+)\]$/);
      cur = m ? cur[m[1]]?.[Number(m[2])] : cur[part];
    }
    if (cur !== undefined && cur !== null && cur !== '') return cur;
  }
  return undefined;
}

interface PFTransaction {
  submissionId: string;
  fileName: string;
  created: string;
  userName: string;
  insuranceCompanyName: string;
  status: string;
  responseJson?: string;
  isLiked?: boolean | null;
  totalCount?: number;
}

const PremiumFinanceScanHistory: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PFTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTx, setPreviewTx] = useState<PFTransaction | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [previewInsured, setPreviewInsured] = useState<PremFiInsuredData | null>(null);

  /* Upload to AMS */
  const [uploadAmsOpen, setUploadAmsOpen] = useState(false);
  const [uploadAmsTxId, setUploadAmsTxId] = useState('');
  const [uploadAmsFileName, setUploadAmsFileName] = useState('');
  const [uploadAmsClientQuery, setUploadAmsClientQuery] = useState('');
  const [uploadAmsClientList, setUploadAmsClientList] = useState<NowCertsClient[]>([]);
  const [uploadAmsClientLoading, setUploadAmsClientLoading] = useState(false);
  const [uploadAmsSelectedClient, setUploadAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [uploadAmsLoading, setUploadAmsLoading] = useState(false);
  const [uploadAmsError, setUploadAmsError] = useState('');
  const [uploadAmsSuccess, setUploadAmsSuccess] = useState(false);

  /* Feedback */
  const [feedbackTxId, setFeedbackTxId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError, setFeedBackError] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess } = useToast();

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscanhistory?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' }, signal },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data: PFTransaction[] = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(Array.isArray(data) && data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q) ||
      r.insuranceCompanyName?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const closePreview = useCallback(() => {
    setPreviewOpen(false); setPdfUrl(null); setPreviewTx(null); setExtractedData(null);
    setPreviewInsured(null);
  }, []);

  const buildPremFiInsuredFromRaw = (pv: any, submissionId: string): PremFiInsuredData => ({
    insuredFirstName: pick(pv, 'insuredName', 'InsuredName') || '',
    policyNumber: pick(pv, 'policyNumber', 'PolicyNumber', 'grossPremium[0].PolicyNumber') || '',
    premium: pick(pv, 'TotalPremium', 'grossPremium[0].Premium', 'grossPremium[0].premium') || '',
    lineOfBusiness: pick(pv, 'lineOfBusiness', 'LineOfBusiness', 'grossPremium[0].LineOfBusiness') || '',
    term: pick(pv, 'term', 'Term', 'grossPremium[0].Term') || '',
    carrierName: pick(pv, 'carrierName', 'CarrierName', 'grossPremium[0].CarrierName') || '',
    effectiveDate: pick(pv, 'EffectiveDate', 'effectiveDate', 'grossPremium[0].EffectiveDate') || '',
    quoteNumber: pick(pv, 'QuoteNumber', 'quoteNumber') || '',
    downPayment: pick(pv, 'grossPremium[0].DownPayment', 'grossPremium[0].downPayment', 'DownPayment') || '',
    financedAmount: pick(pv, 'grossPremium[0].AmountFinanced', 'grossPremium[0].amountFinanced', 'AmountFinanced') || '',
    effectiveAPR: pick(pv, 'EffectiveAPR', 'effectiveAPR') || '',
    otherTaxes: pick(pv, 'grossPremium[0].Taxes', 'grossPremium[0].taxes') || '',
    otherFees: pick(pv, 'grossPremium[0].Fees', 'grossPremium[0].fees') || '',
    addressLine1: pick(pv, 'addressLine1', 'AddressLine1') || '',
    city: pick(pv, 'city', 'City') || '',
    state: pick(pv, 'state', 'State') || '',
    zipCode: pick(pv, 'zipCode', 'ZipCode') || '',
    submissionId,
    fileUrl: '',
  });

  const onPreviewClick = useCallback(async (tx: PFTransaction) => {
    setPreviewTx(tx);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPdfUrl(null);
    let parsed: any = null;
    try { parsed = JSON.parse(tx.responseJson || '{}'); } catch { parsed = {}; }
    setExtractedData(parsed);
    setPreviewInsured(buildPremFiInsuredFromRaw(parsed, tx.submissionId));
    try {
      const res = await fetch(`${process.env.REACT_APP_DataFabric_Url}/api/OCR/previewdocument`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToolboxToken()}`, 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ TransactionId: tx.submissionId, FileName: tx.fileName, Target: 'PfScan' }),
      });
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      const json = await res.json();
      setPdfUrl(json.fileUrl ?? null);
    } catch { /* silent */ }
    finally { setPreviewLoading(false); }
  }, []);

  /* ── Upload to AMS ── */
  const openUploadAms = useCallback((tx: PFTransaction) => {
    setUploadAmsTxId(tx.submissionId);
    setUploadAmsFileName(tx.fileName);
    setUploadAmsClientQuery(''); setUploadAmsClientList([]); setUploadAmsSelectedClient(null);
    setUploadAmsError(''); setUploadAmsSuccess(false);
    setUploadAmsOpen(true);
  }, []);

  const getAmsClients = useCallback((query: string) => {
    if (!query.trim()) { setUploadAmsClientList([]); return; }
    setUploadAmsClientLoading(true);
    axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist`, {
      params: { searchText: query, nowCertsToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then((r) => { setUploadAmsClientList(r.data || []); setUploadAmsClientLoading(false); })
      .catch((e) => { setUploadAmsClientLoading(false); if (e.response?.status === 401) navigate('/'); });
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => getAmsClients(uploadAmsClientQuery), 400);
    return () => clearTimeout(t);
  }, [uploadAmsClientQuery, getAmsClients]);

  const onUploadToAMSConfirmed = useCallback(async () => {
    if (!uploadAmsSelectedClient) return;
    setUploadAmsLoading(true);
    setUploadAmsError('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/uploadV1`,
        {
          TransactionId: uploadAmsTxId,
          FileName: uploadAmsFileName,
          NCPolicyId: '',
          NCInsuredId: uploadAmsSelectedClient.databaseId,
          FileData: '',
          FolderName: 'PremiumFinanceScan',
          TargetName: 'PremiumFinanceScan',
          NowCertsToken: getNcToken(),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      setUploadAmsLoading(false);
      if (res.data.success) {
        setUploadAmsSuccess(true);
        setTimeout(() => { setUploadAmsOpen(false); setUploadAmsSuccess(false); }, 1500);
      } else {
        setUploadAmsError(res.data.errorMessage || 'Upload failed.');
      }
    } catch (err: any) {
      setUploadAmsLoading(false);
      if (err.response?.status === 401) navigate('/');
      else setUploadAmsError('Error while uploading PDF to AMS.');
    }
  }, [uploadAmsTxId, uploadAmsFileName, uploadAmsSelectedClient, navigate]);

  /* ── Feedback ── */
  const openFeedback = useCallback((tx: PFTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(tx.submissionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null); setFeedbackAnchor(null); setFeedbackText(''); setFeedbackRating(null); setFeedBackError('');
  }, []);

  const saveRating = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscan/isliked`,
      { TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); })
     .catch((err: any) => { if (err.response?.status === 401) navigate('/'); });
  }, [navigate, closeFeedback, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find((r) => r.submissionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows((prev) => prev.map((r) => r.submissionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find((r) => r.submissionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows((prev) => prev.map((r) => r.submissionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscan/feedback`,
      { TransactionId: feedbackTxId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch((err: any) => { setFeedbackSubmitting(false); if (err.response?.status === 401) navigate('/'); });
  }, [feedbackTxId, feedbackText, closeFeedback, navigate, showSuccess]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  /* ── Preview field layout — read-only, driven by previewInsured ── */
  const POLICY_FIELD_DEFS: Array<{ key: keyof PremFiInsuredData; label: string; required?: boolean }> = [
    { key: 'insuredFirstName', label: 'Insured Name', required: true },
    { key: 'policyNumber', label: 'Policy Number', required: true },
    { key: 'premium', label: 'Premium Amount ($)', required: true },
    { key: 'lineOfBusiness', label: 'Coverage Code', required: true },
    { key: 'term', label: 'Term (months)', required: true },
    { key: 'carrierName', label: 'Carrier Name' },
    { key: 'effectiveDate', label: 'Effective Date', required: true },
    { key: 'quoteNumber', label: 'Quote Number' },
    { key: 'downPayment', label: 'Down Payment ($)' },
    { key: 'financedAmount', label: 'Financed Amount ($)' },
    { key: 'effectiveAPR', label: 'Effective APR' },
    { key: 'otherTaxes', label: 'Taxes ($)' },
    { key: 'otherFees', label: 'Fees ($)' },
  ];

  const ADDRESS_FIELD_DEFS: Array<{ key: keyof PremFiInsuredData; label: string; required?: boolean }> = [
    { key: 'addressLine1', label: 'Address Line 1' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State', required: true },
    { key: 'zipCode', label: 'Zip Code', required: true },
  ];

  const fieldWrap = (label: string, node: React.ReactNode, required?: boolean) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </div>
      {node}
    </div>
  );

  const renderPreviewField = (def: { key: keyof PremFiInsuredData; label: string; required?: boolean }) => {
    const value = previewInsured?.[def.key] || '';
    return fieldWrap(def.label, (
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '7px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {value || '—'}
      </div>
    ), def.required);
  };

  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title">PremFi (PFA) Scan History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiFileText size={28} />
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching PremFi scan records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search' : 'No PremFi scans yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">File Name</th>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">Username</th>
                    <th className="qp-table__th">Insurance Company</th>
                    <th className="qp-table__th">Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Actions</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => {
                    const isSuccess = tx.status === 'Success';
                    return (
                      <tr key={tx.submissionId ?? i} className="qp-table__row">
                        <td className="qp-table__td" style={{ color: 'var(--accent-purple-light)', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tx.fileName}
                        </td>
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(tx.created ?? '')}</td>
                        <td className="qp-table__td">{tx.userName}</td>
                        <td className="qp-table__td">{showCompanyName(tx.insuranceCompanyName)}</td>
                        <td className="qp-table__td">
                          <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[tx.status] || STATUS_CONFIG.Failed).color }}>
                            {(STATUS_CONFIG[tx.status] || { label: tx.status }).label}
                          </span>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            {isSuccess ? (
                              <button className="qp-table__action-btn" title="Preview" onClick={() => onPreviewClick(tx)}>
                                <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Not Available</span>
                            )}
                          </div>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button className="qp-table__action-btn" title="Feedback" onClick={(e) => openFeedback(tx, e)}>
                              <FiMessageSquare size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) => p === '…'
                  ? <span key={`ellipsis-${i}`} className="qp-pagination__ellipsis">…</span>
                  : <button key={p} className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>,
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={4} />
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedbackClick}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── Upload In AMS Dialog ── */}
      {uploadAmsOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setUploadAmsOpen(false)}>
          <div className="csams-modal" onClick={(e) => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Upload In AMS</h3>
              <button className="csams-modal__close" onClick={() => setUploadAmsOpen(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <label className="csams-label">Search Client</label>
              <div className="csams-search-wrap">
                <input
                  className="csams-search-input"
                  placeholder="Enter client name..."
                  value={uploadAmsClientQuery}
                  onChange={(e) => { setUploadAmsClientQuery(e.target.value); setUploadAmsSelectedClient(null); }}
                  autoFocus
                />
                {uploadAmsClientLoading && <span className="csams-search-spinner" />}
                {uploadAmsClientList.length > 0 && !uploadAmsSelectedClient && (
                  <ul className="csams-dropdown">
                    {uploadAmsClientList.map((c) => (
                      <li
                        key={c.databaseId}
                        className="csams-dropdown__item"
                        onClick={() => { setUploadAmsSelectedClient(c); setUploadAmsClientQuery(c.name); setUploadAmsClientList([]); }}
                      >
                        {c.name}
                        {c.eMail && <span className="csams-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {uploadAmsSuccess && <p className="csams-success">PDF uploaded to AMS successfully!</p>}
              {uploadAmsError && <p className="csams-error">{uploadAmsError}</p>}
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setUploadAmsOpen(false)}>Cancel</button>
              <button
                className="csams-btn csams-btn--upload"
                disabled={!uploadAmsSelectedClient || uploadAmsLoading}
                onClick={onUploadToAMSConfirmed}
              >
                {uploadAmsLoading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview Modal ── */}
      {previewOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={closePreview} style={{ zIndex: 1100, background: 'rgba(0,0,0,0.55)' }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '92vw', maxWidth: 1400, height: '90vh', display: 'flex', flexDirection: 'column',
              borderRadius: 12, background: 'var(--bg-primary)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden', border: '1px solid var(--border-glass)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid var(--border-glass)', flexShrink: 0, background: 'var(--surface-glass)' }}>
              <span style={{ fontSize: 13, fontFamily: "'Poppins', sans-serif" }}>
                <strong style={{ color: 'var(--accent-purple)' }}>File:</strong>{' '}
                <span style={{ color: 'var(--text-primary)' }}>{previewTx?.fileName ?? '—'}</span>
              </span>
              <button onClick={closePreview} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 6 }}>✕</button>
            </div>

            <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              <div className="cspr-pdf-panel">
                <div className="cspr-pdf-panel__header">
                  <span className="cspr-pdf-panel__title">Uploaded PDF</span>
                  <button className="cspr-action-btn cspr-action-btn--purple" disabled={!previewTx} onClick={() => previewTx && openUploadAms(previewTx)}>
                    Upload In AMS
                  </button>
                </div>
                <div className="cspr-pdf-viewer">
                  {pdfUrl
                    ? <iframe src={`${pdfUrl}#zoom=90`} title="PDF Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                    : previewLoading
                      ? <div className="cspr-pdf-placeholder">Loading PDF…</div>
                      : <div className="cspr-pdf-placeholder">PDF not available</div>}
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                {previewLoading && !extractedData ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading extracted data…</p>
                ) : !previewInsured || Object.values(previewInsured).every((v) => !v) ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No data available.</p>
                ) : (
                  <>
                    <div style={{ display: 'flex' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)',
                        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderBottom: 'none',
                        borderRadius: '10px 10px 0 0', padding: '9px 18px',
                      }}>
                        Insured Details
                      </span>
                    </div>

                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: 14, padding: 16,
                      border: '1px solid var(--border-glass)', borderRadius: '0 10px 10px 10px',
                      background: 'rgba(255,255,255,0.03)',
                    }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)', margin: 0, borderBottom: '1px solid var(--border-glass)', paddingBottom: 8 }}>
                        Policy Information
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '14px 14px' }}>
                        {POLICY_FIELD_DEFS.map((def) => (
                          <React.Fragment key={def.key}>{renderPreviewField(def)}</React.Fragment>
                        ))}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)', margin: 0 }}>
                          Address Information
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '14px 14px' }}>
                          {ADDRESS_FIELD_DEFS.map((def) => (
                            <React.Fragment key={def.key}>{renderPreviewField(def)}</React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default PremiumFinanceScanHistory;
